import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from '../store/features/notifications';
import {
  groupNotificationsForInbox,
  getNotificationVisualLead,
  getNotificationDisplayText,
} from '../components/dashboardcomponents/Notifications/notificationHelpers';
import { setKbGroupId } from '../utils/kbGroupId';
import { TICKETS_HOME_PATH } from '../config/featureFlags';

/**
 * Shared inbox logic used by the header NotificationPopup and the
 * sidebar Notifications overlay page.
 */
export default function useNotificationInbox({ open, onNavigateAway } = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    items,
    listLoading,
    listLoadingMore,
    listError,
    page,
    totalPages,
    total,
    unreadCount,
  } = useSelector((state) => state.notifications);

  const loadPage = useCallback(
    (nextPage, append) => {
      dispatch(
        fetchNotifications({
          page: nextPage,
          limit: 20,
          append,
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    if (open) {
      loadPage(1, false);
    }
  }, [open, loadPage]);

  const hasMorePages =
    (totalPages > 0 && page < totalPages) ||
    (total > 0 && items.length < total);

  const handleSeeMore = () => {
    if (listLoadingMore || listLoading || !hasMorePages) return;
    loadPage(page + 1, true);
  };

  const handleNotificationClick = (n) => {
    const { referenceModel, referenceId, metadata, _id } = n;
    const meta = metadata || {};
    const notifGroupId = meta.groupId || meta.group_id || null;
    onNavigateAway?.();

    if (notifGroupId) {
      setKbGroupId(notifGroupId);
    }

    if (referenceModel === 'Ticket' || referenceModel === 'TicketMessage') {
      const ticketId =
        referenceModel === 'TicketMessage'
          ? meta.ticketId ?? referenceId
          : referenceId;
      if (ticketId) {
        navigate(`${TICKETS_HOME_PATH}?ticketId=${ticketId}`);
      }
    } else if (referenceModel === 'Room' || referenceModel === 'Message') {
      const roomId = meta.roomId ?? referenceId;
      navigate(`/Live?roomId=${roomId}`);
    } else if (n.type === 'kb_article_pending_review') {
      const articleId = meta.articleId ?? referenceId;
      if (articleId) {
        const params = new URLSearchParams({ articleId });
        if (notifGroupId) params.set('group_id', notifGroupId);
        navigate(`/kb/review-queue?${params.toString()}`);
      }
    } else if (
      referenceModel === 'KBFeedbackQueue' ||
      n.type === 'kb_feedback_received' ||
      n.type === 'kb_feedback_assigned'
    ) {
      const feedbackId = referenceId || meta.feedbackId;
      if (feedbackId) {
        const params = new URLSearchParams({ feedbackId });
        if (notifGroupId) params.set('group_id', notifGroupId);
        navigate(`/kb/feedback-queue?${params.toString()}`);
      }
    } else {
      const kbArticleId =
        meta.articleId ??
        (referenceModel === 'KBArticle' ? referenceId : null);
      if (kbArticleId) {
        navigate(`/kb/article/${kbArticleId}`);
      }
    }

    if (_id && !n.isRead) {
      dispatch(markNotificationRead(_id));
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await dispatch(markAllNotificationsRead()).unwrap();
    } catch {
      /* keep UI; errors surface via global patterns if needed */
    }
    dispatch(fetchNotifications({ page: 1, limit: 20, append: false }));
  };

  const { new: sectionNew, today, yesterday, olderSections } =
    groupNotificationsForInbox(items);

  const mapToItemProps = (n) => {
    const lead = getNotificationVisualLead(n);
    const { title, body } = getNotificationDisplayText(n);
    const useAvatar = lead.kind === 'avatar' && lead.name;
    return {
      ...n,
      id: n._id,
      title,
      description: body,
      body,
      iconType: useAvatar ? undefined : lead.iconType,
      name: useAvatar ? lead.name : undefined,
      isRead: n.isRead,
    };
  };

  const sectionBlocks = [];
  if (sectionNew.length > 0) {
    sectionBlocks.push({ key: 'new', items: sectionNew });
  }
  if (today.length > 0) {
    sectionBlocks.push({ key: 'today', items: today });
  }
  if (yesterday.length > 0) {
    sectionBlocks.push({ key: 'yesterday', items: yesterday });
  }
  (olderSections || []).forEach(({ dateKey, label, items: olderItems }) => {
    sectionBlocks.push({
      key: `older-${dateKey}`,
      items: olderItems,
      label,
    });
  });

  const hasAny =
    sectionNew.length > 0 ||
    today.length > 0 ||
    yesterday.length > 0 ||
    (olderSections || []).some((s) => s.items?.length > 0);

  return {
    items,
    listLoading,
    listLoadingMore,
    listError,
    unreadCount,
    hasMorePages,
    hasAny,
    sectionBlocks,
    mapToItemProps,
    handleSeeMore,
    handleNotificationClick,
    handleMarkAllRead,
  };
}
